import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, Plus, Trash2, X, Clock, Pencil, Check } from 'lucide-react';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: Convex generated dataModel lives outside src/
import type { TCompanyMemberRole } from '@/@types/company';

// LOCAL_TYPE_JUSTIFIED: inline query result shape — not a domain type, tightly coupled to this component's props
interface ITeamMember {
  _id: Id<'companyMembers'>;
  companyId: Id<'companies'>;
  userId: Id<'users'>;
  roles: TCompanyMemberRole[];
  employeeRecordId?: Id<'companyEmployees'>;
  joinedAt: number;
  userName?: string;
  userEmail?: string;
}

// LOCAL_TYPE_JUSTIFIED: inline invitation shape from listByCompany query
interface IPendingInvitation {
  _id: Id<'companyInvitations'>;
  email: string;
  roles: TCompanyMemberRole[];
  status: string;
  createdAt: number;
  expiresAt: number;
}

interface ITeamMembersSectionProps {
  members: ITeamMember[] | undefined;
  pendingInvitations?: IPendingInvitation[];
  onInvite?: () => void;
  onRemove?: (memberId: Id<'companyMembers'>) => void;
  onUpdateMemberName?: (targetUserId: Id<'users'>, name: string) => Promise<void>;
  onCancelInvitation?: (invitationId: Id<'companyInvitations'>) => void;
}

function RoleBadge({ role }: { role: TCompanyMemberRole }) {
  const variant = role === 'admin' ? 'default' : 'secondary';
  return (
    <Badge variant={variant}>
      {role.charAt(0).toUpperCase() + role.slice(1)}
    </Badge>
  );
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function TeamMembersSection({
  members,
  pendingInvitations,
  onInvite,
  onRemove,
  onUpdateMemberName,
  onCancelInvitation,
}: ITeamMembersSectionProps) {
  const hasPending = pendingInvitations && pendingInvitations.length > 0;
  const [editingUserId, setEditingUserId] = useState<Id<'users'> | null>(null);
  const [editingName, setEditingName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const startEditing = (userId: Id<'users'>, currentName: string) => {
    setEditingUserId(userId);
    setEditingName(currentName);
  };

  const cancelEditing = () => {
    setEditingUserId(null);
    setEditingName('');
  };

  const handleSaveName = async () => {
    if (!editingUserId || !editingName.trim() || !onUpdateMemberName) return;
    setIsSaving(true);
    try {
      await onUpdateMemberName(editingUserId, editingName.trim());
      cancelEditing();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="rounded-lg bg-primary/10 p-2">
                <Users className="w-4 h-4 text-foreground" />
              </div>
              Team Members ({members?.length ?? 0})
            </CardTitle>
            {onInvite && (
              <Button variant="outline" size="sm" onClick={onInvite}>
                <Plus aria-hidden="true" className="w-3.5 h-3.5 mr-1.5" />
                Invite
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {members === undefined && (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          )}
          {members !== undefined && members.length === 0 && !hasPending && (
            <p className="text-sm text-muted-foreground text-center py-8">
              No team members yet. Invite someone to get started.
            </p>
          )}
          {members !== undefined && members.length > 0 && (
            <div className="space-y-2">
              {members.map(member => (
                <div
                  key={member._id}
                  className="flex items-center justify-between p-3 border border-border rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    {editingUserId === member.userId ? (
                      <div className="flex items-center gap-1">
                        <Input
                          value={editingName}
                          onChange={e => setEditingName(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') void handleSaveName();
                            if (e.key === 'Escape') cancelEditing();
                          }}
                          className="h-8 w-48 text-sm"
                          maxLength={100}
                          autoFocus
                          aria-label={`Edit name for ${member.userEmail ?? 'member'}`}
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 shrink-0"
                          onClick={() => void handleSaveName()}
                          disabled={isSaving || !editingName.trim()}
                          aria-label="Save name"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 shrink-0"
                          onClick={cancelEditing}
                          aria-label="Cancel editing"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium text-foreground truncate">
                          {member.userName ?? <span className="text-muted-foreground italic">Unknown</span>}
                        </p>
                        {onUpdateMemberName && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 shrink-0"
                            onClick={() => startEditing(member.userId, member.userName ?? '')}
                            aria-label={`Edit name for ${member.userName ?? 'member'}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground truncate">
                      {member.userEmail ?? '—'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <div className="flex gap-1.5 flex-wrap">
                      {member.roles.map(role => (
                        <RoleBadge key={role} role={role} />
                      ))}
                    </div>
                    {onRemove && !member.roles.includes('admin') && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
                        onClick={() => onRemove(member._id)}
                        aria-label={`Remove ${member.userName ?? 'member'}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {hasPending && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="rounded-lg bg-primary/10 p-2">
                <Clock className="w-4 h-4 text-foreground" />
              </div>
              Pending Invitations ({pendingInvitations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingInvitations.map(inv => (
                <div
                  key={inv._id}
                  className="flex items-center justify-between p-3 border border-border rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {inv.email}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Invited {formatDate(inv.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <div className="flex gap-1.5 flex-wrap">
                      {inv.roles.map(role => (
                        <RoleBadge key={role} role={role} />
                      ))}
                    </div>
                    <Badge variant="outline">Invited</Badge>
                    {onCancelInvitation && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0"
                        onClick={() => onCancelInvitation(inv._id)}
                        aria-label={`Cancel invitation for ${inv.email}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
